import * as React from 'react';

import {motion} from 'framer-motion';

import {Button} from '../button/button';
import {CloseButton} from '../button/close-button/close-button';
import {Card} from '../card/card';

import styles from './popup-notification.module.css';

const ANIMATE_DURATION_MS = 500; // in milliseconds
const ANIMATE_DURATION_S = ANIMATE_DURATION_MS / 1000; // in seconds
const DEFAULT_WIDTH = 400;

type Props = {
    icon: React.ComponentType<any>;
    iconColor?: string;
    title: string;
    body: string | React.ReactNode;
    onClose: () => any;
    actionButton?: {
        text: string;
        onClick: () => any;
        isNotificationClickTarget?: boolean;
    };
    width?: number;
};

export function PopupNotification(props: Props) {
    const {icon: Icon, title, body, onClose, actionButton, width = DEFAULT_WIDTH} = props;

    const [isAnimatingOut, setIsAnimatingOut] = React.useState<boolean>(false);
    const [isAnimationComplete, setIsAnimationComplete] = React.useState<boolean>(false);

    return (
        <div className={styles['notification-container']}>
            <Card hasBorder={!isAnimationComplete} hasShadow={true} fitContent={true}>
                <motion.div
                    initial='hidden'
                    exit='hidden'
                    animate={isAnimatingOut ? 'hidden' : 'visible'}
                    variants={{
                        visible: {width: 'auto', height: 'auto'},
                        hidden: {width: 0, height: 0},
                    }}
                    transition={{
                        type: 'tween',
                        duration: ANIMATE_DURATION_S,
                        ease: 'easeInOut',
                    }}
                >
                    <div className={styles.notification}>
                        <div className={styles['close-button']}>
                            <CloseButton
                                size={11}
                                onClick={() => {
                                    setIsAnimatingOut(true);
                                    // Perform onClose action after animation completes
                                    setTimeout(() => {
                                        setIsAnimationComplete(true);
                                        onClose();
                                    }, ANIMATE_DURATION_MS);
                                }}
                            />
                        </div>
                        <div
                            style={{width}}
                            onClick={
                                actionButton && actionButton.isNotificationClickTarget
                                    ? () => {
                                          actionButton.onClick();
                                      }
                                    : undefined
                            }
                            className={
                                actionButton && actionButton.isNotificationClickTarget
                                    ? styles['content--clickable']
                                    : styles.content
                            }
                        >
                            <Icon
                                size={32}
                                wrapWithDiv={false}
                                color={props.iconColor}
                                fill={props.iconColor}
                            />
                            <div className={styles.message}>
                                <strong>{title}</strong>
                                <div>{body}</div>
                            </div>
                            {actionButton ? (
                                <div className={styles['action-button']}>
                                    <Button variant='text-primary' onClick={actionButton.onClick}>
                                        {actionButton.text}
                                    </Button>
                                </div>
                            ) : undefined}
                        </div>
                    </div>
                </motion.div>
            </Card>
        </div>
    );
}
